import { useState, useEffect, SyntheticEvent } from "react";
import axios from "axios";
import { Link, useNavigate } from 'react-router-dom';
import moment from "moment-timezone";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import PlaylistAddOutlined from '@mui/icons-material/PlaylistAddOutlined';
import { LoadingButton, MoneyField, PageHeader } from '@routes-plan/ui';
import { Route } from "../../../models/route";
import { ExpenseType } from "../../../models/expense-type";
import { OperationType } from "../../../models/operation-type";
import { useSelection } from '../../../components/context/SelectionContext';
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";
import { operationalTimezone } from '../../../utils/operationalDateTime';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { companyCurrency, companyLocale, formatCurrency } from '../../../utils/format/currency';
import OfficeRouteSelects from '../components/OfficeRouteSelects';
import { formActionsSx, formGridSx, formMaxWidthSx, pageSx, sectionSx } from '../components/layout';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const CashBoxExpenseNew: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  // ** States
  const t = useT();
  setPageTitle('cashBoxes.newTitle');
  const [saving, setSaving] = useState(false);
  const { canView: canViewMainCashMovements } = useResourcePermissions('/loans/cash-boxes');
  const [operationTypeId, setOperationTypeId] = useState("");
  const [cashBoxId, setCashBoxId] = useState("");
  const [balance, setBalance] = useState("");
  const [routeBalance, setRouteBalance] = useState("");
  const [officeRoutes, setOfficeRoutes] = useState([]);
  const [cashBox, setCashBox] = useState([]);
  const [officeCashBoxId, setOfficeCashBoxId] = useState<number | null>(null);
  const [routeCashBoxId, setRouteCashBoxId] = useState<number | null>(null);
  const [value, setValue] = useState("");
  const [description, setDescripcion] = useState("");
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [expenseTypeId, setExpenseTypeId] = useState<number | null>(null);
  const [operation, setOperation] = useState([]);
  const [offices, setOffices] = useState([]);
  const [isOutflow, setIsOutflow] = useState(false);
  // En esta vista no se permite "retirar de la ruta". Solo se permite enviar a una ruta.
  // Por lo tanto, no se exponen switches: la dirección queda fija.
  const { office_id: officeId, setOffice_id: setOfficeId, collect_id: routeId, setCollect_id: setRouteId, user_id: userId, setUser_id: setUserId } = useSelection();

  const [originCashBoxId, setOriginCashBoxId] = useState<number | null>(null);
  const [destinationCashBoxId, setDestinationCashBoxId] = useState<number | null>(null);

  useEffect(() => {
    // Dirección fija:
    // - operationTypeId=2: ENVIAR a una ruta (origen sede -> destino ruta)
    // - otros: comportamiento existente (origen sede, sin destino por defecto)
    if (operationTypeId === "2") {
      setOriginCashBoxId(officeCashBoxId);
      setDestinationCashBoxId(routeCashBoxId);
      return;
    }
    setOriginCashBoxId(officeCashBoxId);
    setDestinationCashBoxId(null);
  }, [operationTypeId, routeCashBoxId, officeCashBoxId]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!officeId || !operationTypeId || !value || !expenseTypeId) {
      showAlert(t('cashBoxes.requiredFields'), 'warning');
      return;
    }
    // Solo se permite enviar a una ruta (operationTypeId=2), por lo que aquí sí se exige la ruta.
    if (operationTypeId === "2" && !routeId) {
      showAlert(t('cashBoxes.selectRouteWarning'), 'warning');
      return;
    }

    const expenseDate = moment().tz(operationalTimezone()).format('YYYY-MM-DD');
    const formData = {
      description: description,
      value: parseInt(value),
      originCashBoxId: originCashBoxId,
      destinationCashBoxId: destinationCashBoxId,
      expenseTypeId: expenseTypeId,
      userId,
      operationTypeId: Number(operationTypeId),
      date: expenseDate,
      isOutflow: isOutflow,
    };
    const url = "/cash-movements";

    setSaving(true);
    try {
      const response = await axios.post(url, formData);
      showAlert(t('cashBoxes.movementSaved'), 'success');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate('/loans/cash-boxes');
    } catch (error) {
      showAlert(t('common.saveError'), 'error');
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  const fetchBalance = async (officeId: string) => {
    try {
      const { data } = await axios.get(`/offices/${officeId}/cash-box`);
      setBalance(data.data[0].balance);
      setOfficeCashBoxId(data.data[0].id);
    } catch (error) {
      showAlert(t('cashBoxes.officeBalanceError'), 'error');
    }
  };

  const fetchRouteBalance = async (balanceRouteId: number) => {
    showLoading();
    try {
      const { data } = await axios.get(`/routes/${balanceRouteId}/cash-box`);
      const first = data?.data?.[0];
      if (!first) {
        setRouteBalance("0");
        setRouteCashBoxId(null);
        return;
      }
      setRouteBalance(first.balance);
      setRouteCashBoxId(first.id);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Esperar 1 segundo
    } catch (error) {
      const serverMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.error;
      showAlert(
        serverMessage || t('cashBoxes.routeBalanceError'),
        'error',
      );
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const [
          { data: userData },
          { data: officesData },
          { data: operationData },
        ] = await Promise.all([
          axios.get("user"),
          axios.get("offices"),
          axios.get("typeoperation"),
        ]);
        setUserId(userData.data.id);
        setOffices(officesData.data);
        setOperation(operationData.data);

        await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos
      } catch (error) {
        // showAlert('Alerta recuperar la información del usaurio!', 'warning');
        //console.log('Alerta recuperar la información del usaurio:', error);
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (officeId) {
        showLoading();
        try {
          const [
            { data: boxData },
            { data: routesData },
          ] = await Promise.all([
            axios.get(`/offices/${officeId}/cash-box`),
            axios.get(`offices/${officeId}/routes`),
          ]);

          if (boxData.data.length > 0) {
            setCashBox(boxData.data[0]);
            setCashBoxId(boxData.data[0].id);
            await fetchBalance(officeId); // Fetch balance after setting box data
          } else {
            showAlert(t('cashBoxes.officeWithoutCashBox'), 'warning');
          }
          setOfficeRoutes(routesData);
          setRouteId(""); // Limpiar la ruta seleccionada
          setRouteBalance("0");
          setRouteCashBoxId(null);
        } catch (error) {
          showAlert(t('common.loadError'), 'error');
        } finally {
          hideLoading();
        }
      }
    };
    fetchData();
  }, [officeId]);

  useEffect(() => {
    if (routeId) {
      fetchRouteBalance(Number(routeId)); // Convertir routeId a número
    }
  }, [routeId]);

  useEffect(() => {
    if (operationTypeId) {
      (async () => {
        try {
          const url = "/expense-types/search";
          const formDatas = {
            operationTypeId: Number(operationTypeId),
            categoryId: 2
          }
          // const { data } = await axios.get(`operation-types/${operationTypeId}/expense-types`);
          const { data } = await axios.post(url, formDatas);
          setExpenses(data.data);
          setExpenseTypeId(null); // Reiniciar el campo Detalle Operación
        } catch (error) {
          showAlert(t('cashBoxes.expenseTypesError'), 'error');
        }
      })();
    } else {
      setExpenses([]);
      setExpenseTypeId(null);
    }
  }, [operationTypeId]);

  useEffect(() => {
    if (operationTypeId === "2") {
      setIsOutflow(true);
    } else {
      setRouteId("");
      setRouteBalance("0");
      setRouteCashBoxId(null);
      setDestinationCashBoxId(null);
      setIsOutflow(false);
    }
  }, [operationTypeId]);

  return (
    <Box sx={pageSx}>
      <PageHeader
        title={t('cashBoxes.newTitle')}
        description={t('cashBoxes.newDescription')}
        breadcrumbs={[
          { label: t('cashBoxes.listTitle'), href: '#/loans/cash-boxes' },
          { label: t('cashBoxes.newTitle') },
        ]}
        actions={
          <>
            <PermissionButton
              allowed={canViewMainCashMovements}
              deniedTooltip={t('cashBoxes.noPermissionViewMovements')}
              component={Link}
              to="/loans/cash-boxes"
              variant="outlined"
              startIcon={<ListAltOutlined />}
            >
              {t('cashBoxes.listTitle')}
            </PermissionButton>
            <Button component={Link} to="/loans/expenses/new" variant="outlined" startIcon={<PlaylistAddOutlined />}>
              {t('cashBoxes.registerExpense')}
            </Button>
          </>
        }
      />
      <Box component="form" onSubmit={submit} sx={{ ...formMaxWidthSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper variant="outlined" sx={sectionSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('cashBoxes.sectionOrigin')}
          </Typography>
          <Box sx={formGridSx}>
            <OfficeRouteSelects offices={offices} officeId={officeId} onOfficeChange={setOfficeId} required />
            <TextField fullWidth size="small" label={t('cashBoxes.officeBalance')} value={formatCurrency(balance || 0)} InputProps={{ readOnly: true }} />
            <TextField
              select
              fullWidth
              size="small"
              required
              label={t('cashBoxes.operation')}
              value={operationTypeId}
              onChange={(event) => setOperationTypeId(event.target.value)}
            >
              {operation.map((op: OperationType) => (
                <MenuItem key={op.id} value={op.id.toString()}>
                  {op.name}
                </MenuItem>
              ))}
            </TextField>
            {operationTypeId === "2" ? (
              <TextField
                select
                fullWidth
                size="small"
                required
                label={t('cashBoxes.destinationRoute')}
                value={routeId ? routeId.toString() : ""}
                onChange={(event) => setRouteId(event.target.value)}
              >
                {officeRoutes.map((route: Route) => (
                  <MenuItem key={route.id} value={route.id.toString()}>
                    {route.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {operationTypeId === "2" ? (
              <TextField fullWidth size="small" label={t('cashBoxes.routeBalance')} value={formatCurrency(routeBalance || 0)} InputProps={{ readOnly: true }} />
            ) : null}
          </Box>
        </Paper>
        <Paper variant="outlined" sx={sectionSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('cashBoxes.sectionMovement')}
          </Typography>
          <Box sx={formGridSx}>
            <TextField
              select
              fullWidth
              size="small"
              required
              disabled={!operationTypeId}
              helperText={!operationTypeId ? t('cashBoxes.chooseOperationFirst') : undefined}
              label={t('cashBoxes.expenseType')}
              value={expenseTypeId !== null ? expenseTypeId.toString() : ""}
              onChange={(event) => setExpenseTypeId(parseInt(event.target.value))}
            >
              {expenses.map((expense: ExpenseType) => (
                <MenuItem key={expense.id} value={expense.id.toString()}>
                  {expense.name}
                </MenuItem>
              ))}
            </TextField>
            <MoneyField
              fullWidth
              size="small"
              required
              label={t('cashBoxes.value')}
              value={value === '' ? null : Number(value)}
              onChange={(v) => setValue(v == null ? '' : String(v))}
              currency={companyCurrency()}
              locale={companyLocale()}
            />
            <TextField
              fullWidth
              size="small"
              label={t('cashBoxes.description')}
              placeholder={t('cashBoxes.descriptionPlaceholder')}
              onChange={(event) => setDescripcion(event.target.value)}
              inputProps={{ maxLength: 100 }}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
        </Paper>
        <Box sx={formActionsSx}>
          <Button variant="outlined" component={Link} to="/loans/cash-boxes">
            {t('common.cancel')}
          </Button>
          <LoadingButton type="submit" variant="contained" loading={saving}>
            {t('common.save')}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CashBoxExpenseNew;
